<template>
  <header class="navbar">
    <div class="navbar-content">
      <!-- Logo 区域 -->
      <a href="#/" class="navbar-logo">
        <img src="@/assets/logo.png" alt="UpGame Logo" class="logo-image" />
      </a>

      <!-- 中间导航菜单 -->
      <nav class="navbar-menu">
        <a href="#/" class="nav-link">{{ t('nav.home') }}</a>

        <!-- 游戏下拉菜单 -->
        <div class="games-dropdown">
          <button
            class="games-toggle"
            @click="toggleGameDropdown"
            aria-label="Select game"
            :aria-expanded="gameDropdownOpen"
          >
            <span class="games-label">{{ t('nav.games') }}</span>
            <svg class="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <!-- 游戏选项菜单 -->
          <div v-if="gameDropdownOpen" class="games-menu">
            <button
              v-for="game in gameOptions"
              :key="game.code"
              class="games-option"
              @click="selectGame(game.code)"
            >
              <span class="game-name">{{ t(`games.${game.code}.title`) }}</span>
              <span class="game-desc">{{ t(`games.${game.code}.description`) }}</span>
            </button>
          </div>
        </div>

        <a href="#/leaderboard" class="nav-link">{{ t('nav.leaderboard') }}</a>
        <a href="#/strategies" class="nav-link">{{ t('nav.strategies') }}</a>
        <a href="#/about" class="nav-link">{{ t('nav.about') }}</a>
      </nav>

      <!-- 右侧工具区 -->
      <div class="navbar-actions">
        <!-- 语言选择器下拉菜单 -->
        <div class="language-dropdown">
          <button
            class="language-toggle"
            @click="toggleLanguageDropdown"
            aria-label="Select language"
            :aria-expanded="languageDropdownOpen"
          >
            <span class="language-label">{{ currentLanguageLabel }}</span>
            <svg class="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <!-- 下拉菜单选项 -->
          <div v-if="languageDropdownOpen" class="language-menu">
            <button
              v-for="lang in languageOptions"
              :key="lang.code"
              :class="['language-option', { active: currentLang === lang.code }]"
              @click="setLanguage(lang.code)"
            >
              {{ lang.nativeLabel }}
            </button>
          </div>
        </div>

        <!-- 主题切换（暗黑模式） -->
        <button class="theme-toggle" @click="toggleTheme" aria-label="Toggle dark mode">
          <svg v-if="!isDarkMode" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
          <svg v-else class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        </button>

        <!-- 移动菜单按钮 -->
        <button class="mobile-menu-btn hide-desktop" @click="toggleMobileMenu" aria-label="Toggle menu">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line v-if="!mobileMenuOpen" x1="3" y1="6" x2="21" y2="6"></line>
            <line v-if="!mobileMenuOpen" x1="3" y1="12" x2="21" y2="12"></line>
            <line v-if="!mobileMenuOpen" x1="3" y1="18" x2="21" y2="18"></line>
            <line v-if="mobileMenuOpen" x1="18" y1="6" x2="6" y2="18"></line>
            <line v-if="mobileMenuOpen" x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- 移动菜单 -->
    <nav v-if="mobileMenuOpen" class="mobile-menu hide-desktop">
      <a href="#/" class="mobile-nav-link" @click="closeMobileMenu">
        {{ t('nav.home') }}
      </a>
      <div class="mobile-menu-section">
        <div class="mobile-menu-section-title">{{ t('nav.games') }}</div>
        <a href="#/games/poker" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('games.poker.title') }}
        </a>
        <a href="#/games/sudoku" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('games.sudoku.title') }}
        </a>
        <a href="#/games/riddles" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('games.riddles.title') }}
        </a>
      </div>
      <a href="#/leaderboard" class="mobile-nav-link" @click="closeMobileMenu">
        {{ t('nav.leaderboard') }}
      </a>
      <a href="#/strategies" class="mobile-nav-link" @click="closeMobileMenu">
        {{ t('nav.strategies') }}
      </a>
      <a href="#/about" class="mobile-nav-link" @click="closeMobileMenu">
        {{ t('nav.about') }}
      </a>
      <div class="mobile-menu-section">
        <div class="mobile-menu-section-title">{{ t('footer.resources') }}</div>
        <a href="#/how-to-play" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('nav.how') }}
        </a>
        <a href="#/tips" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('nav.tips') }}
        </a>
        <a href="#/faq" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('nav.faq') }}
        </a>
        <a href="#/support" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('nav.support') }}
        </a>
      </div>
      <div class="mobile-menu-section">
        <div class="mobile-menu-section-title">{{ t('footer.company') }}</div>
        <a href="#/contact" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('nav.contact') }}
        </a>
        <a href="#/privacy" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('footer.privacy') }}
        </a>
        <a href="#/terms" class="mobile-nav-link indent" @click="closeMobileMenu">
          {{ t('footer.terms') }}
        </a>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from '@/utils/i18n'

const { t, setLanguage: changeLanguage, currentLang } = useI18n()

const mobileMenuOpen = ref(false)
const isDarkMode = ref(false)
const languageDropdownOpen = ref(false)
const gameDropdownOpen = ref(false)

// 语言选项
const languageOptions = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'zh', label: '中文', nativeLabel: '中文' },
  { code: 'fr', label: 'Français', nativeLabel: 'Français' }
]

// 游戏选项
const gameOptions = [
  { code: 'poker' },
  { code: 'sudoku' },
  { code: 'riddles' }
]

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

const toggleLanguageDropdown = () => {
  languageDropdownOpen.value = !languageDropdownOpen.value
}

const closeLanguageDropdown = () => {
  languageDropdownOpen.value = false
}

const toggleGameDropdown = () => {
  gameDropdownOpen.value = !gameDropdownOpen.value
}

const selectGame = (gameCode) => {
  window.location.hash = `#/games/${gameCode}`
  gameDropdownOpen.value = false
  closeMobileMenu()
}

const toggleTheme = () => {
  isDarkMode.value = !isDarkMode.value

  // 实现主题切换：添加/移除 dark-mode 类
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark-mode')
    localStorage.setItem('theme-mode', 'dark')
  } else {
    document.documentElement.classList.remove('dark-mode')
    localStorage.setItem('theme-mode', 'light')
  }
}

const setLanguage = (lang) => {
  changeLanguage(lang)
  closeLanguageDropdown()
  closeMobileMenu()
}

// 获取当前语言的显示标签
const currentLanguageLabel = computed(() => {
  const lang = languageOptions.find(l => l.code === currentLang.value)
  return lang ? lang.nativeLabel : 'EN'
})

// 初始化主题模式
onMounted(() => {
  const savedTheme = localStorage.getItem('theme-mode')
  if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true
    document.documentElement.classList.add('dark-mode')
  } else {
    isDarkMode.value = false
    document.documentElement.classList.remove('dark-mode')
  }
})
</script>

<style scoped>
.navbar {
  background-color: var(--color-bg-white);
  box-shadow: var(--shadow-sm);
  position: sticky;
  top: 0;
  z-index: var(--z-fixed);
  transition: all var(--transition-base) var(--ease-in-out);
}

.navbar-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 var(--space-base);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-xl);
}

/* Logo 区域 */
.navbar-logo {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  text-decoration: none;
  color: var(--color-text-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-h4);
  flex-shrink: 0;
  transition: color var(--transition-base) var(--ease-in-out);
}

.navbar-logo:hover {
  color: var(--color-primary-blue);
}

.logo-image {
  width: 120px;
  height: 120px;
  display: block;
}

/* 导航菜单 */
.navbar-menu {
  display: none;
  gap: var(--space-2xl);
  flex: 1;
}

@media (min-width: 1024px) {
  .navbar-menu {
    display: flex;
  }
}

.nav-link {
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-small);
  position: relative;
  transition: color var(--transition-base) var(--ease-in-out);
}

.nav-link:hover {
  color: var(--color-primary-orange);
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--gradient-primary);
  transition: width var(--transition-base) var(--ease-in-out);
}

.nav-link:hover::after {
  width: 100%;
}

.router-link-active::after {
  width: 100%;
}

/* Games dropdown */
.games-dropdown {
  position: relative;
  display: none;
}

@media (min-width: 1024px) {
  .games-dropdown {
    display: block;
  }
}

.games-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
  position: relative;
}

.games-toggle:hover {
  color: var(--color-primary-orange);
}

.games-toggle .dropdown-icon {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-base) var(--ease-in-out);
}

.games-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-bg-white);
  box-shadow: var(--shadow-lg);
  min-width: 220px;
  z-index: 1000;
  animation: slideDown 0.2s ease-out;
}

.games-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  text-align: left;
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
  font-family: inherit;
}

.games-option:hover {
  background-color: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-orange);
  padding-left: 20px;
}

.game-name {
  font-weight: 600;
  font-size: var(--font-size-small);
  margin-bottom: 2px;
}

.game-desc {
  font-size: 12px;
  color: var(--color-text-secondary);
}

/* 动作区 */
.navbar-actions {
  display: flex;
  align-items: center;
  gap: var(--space-base);
  flex-shrink: 0;
}

/* 语言选择器下拉菜单 */
.language-dropdown {
  position: relative;
  display: none;
}

@media (min-width: 768px) {
  .language-dropdown {
    display: block;
  }
}

.language-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-bg-card);
  color: var(--color-text-primary);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
}

.language-toggle:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background-color: rgba(255, 107, 53, 0.05);
}

.language-label {
  min-width: 70px;
  text-align: left;
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-base) var(--ease-in-out);
  flex-shrink: 0;
}

.language-dropdown[data-open] .dropdown-icon {
  transform: rotate(180deg);
}

.language-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-bg-white);
  box-shadow: var(--shadow-lg);
  min-width: 150px;
  z-index: 1000;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.language-option {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  font-size: var(--font-size-small);
  text-align: left;
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
}

.language-option:hover {
  background-color: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-orange);
  padding-left: 20px;
}

.language-option.active {
  background: rgba(255, 107, 53, 0.15);
  color: var(--color-primary-orange);
  font-weight: var(--font-weight-semibold);
}

/* 主题切换 */
.theme-toggle {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out);
  display: none;
}

@media (min-width: 768px) {
  .theme-toggle {
    display: flex;
  }
}

.theme-toggle:hover {
  background-color: var(--color-bg-card);
  color: var(--color-primary-orange);
}

.icon {
  width: 20px;
  height: 20px;
}

/* 移动菜单按钮 */
.mobile-menu-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  cursor: pointer;
}

.mobile-menu-btn svg {
  width: 24px;
  height: 24px;
  stroke-width: 2.5;
}

/* 移动菜单 */
.mobile-menu {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid var(--color-border);
  animation: slideInDown 0.3s ease-out;
}

.mobile-nav-link {
  padding: var(--space-base) var(--space-base);
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: background-color var(--transition-base) var(--ease-in-out);
}

.mobile-nav-link:hover {
  background-color: var(--color-bg-card);
  color: var(--color-primary-orange);
}

.router-link-active.mobile-nav-link {
  background-color: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-orange);
}

.mobile-menu-section {
  border-top: 1px solid var(--color-border);
}

.mobile-menu-section-title {
  padding: var(--space-base);
  background-color: var(--color-bg-card);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mobile-nav-link.indent {
  padding-left: var(--space-2xl);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-small);
}
</style>
